import React, { useState } from "react";
import { Tabs, Collapse, Typography, Table, message } from "antd";
import {
  CheckCircle,
  ArrowRight,
  Code,
  Shield,
  Database,
  Clock,
  Download,
  Lock,
  BookOpen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import SDKDownloadModal from "../components/SDKDownloadModal";
import useUserStore from "../store/userStore";
import { apiService } from "../api/apiService";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// API 示例数据
const apiCategories = [
  {
    id: "auth",
    name: "认证与授权",
    description: "管理用户认证和授权的API",
    endpoints: [
      {
        id: "auth-login",
        method: "POST",
        path: "/api/v1/auth/login",
        summary: "用户登录",
        description: "使用用户名和密码获取访问令牌",
        request: {
          body: {
            username: "string",
            password: "string",
          },
        },
        response: {
          token: "string",
          expires: "string",
          user: {
            id: "string",
            name: "string",
            email: "string",
          },
        },
        example: {
          request: `import socket
import json
import numpy as np

req = {
    "obs": np.zeros(66).tolist(),
    "feedback": np.zeros(1).tolist()
}

s = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
s.connect(("127.0.0.1", 9000))
s.send(json.dumps(req).encode())

resp = s.recv(4096)
print("Response:", resp.decode())`,
          response: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "expires": "2024-12-31T23:59:59Z",
  "user": {
    "id": "user123",
    "name": "Admin User",
    "email": "admin@example.com"
  }
}`,
        },
      },
      {
        id: "auth-logout",
        method: "POST",
        path: "/api/v1/auth/logout",
        summary: "用户登出",
        description: "使当前访问令牌失效",
        example: {
          request: `curl -X POST "/api/v1/auth/logout" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`,
          response: `{
  "message": "Successfully logged out"
}`,
        },
      },
    ],
  },
  {
    id: "ai",
    name: "启动服务",
    description: "提供AI智能分析和处理能力的API",
    endpoints: [
      {
        id: "ai-analyze",
        method: "POST",
        path: "/api/v1/ai/analyze",
        summary: "智能分析",
        description: "分析设备数据并返回智能决策结果",
        request: {
          body: `cd your_current_path/dist/socket_test_arm
                ./socket_test_arm`,
        },
        response: {
          analysisId: "string",
          results: [
            {
              type: "alert",
              message: "Temperature is above threshold",
              severity: "high",
            },
          ],
          recommendations: [
            {
              action: "reduce_power",
              description: "Reduce device power consumption",
            },
          ],
        },
        example: {
          request: `curl -X POST "/api/v1/ai/analyze" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." \
  -H "Content-Type: application/json" \
  -d '{"deviceId": "device001", "data": {"temperature": 85, "humidity": 45, "pressure": 1013}}'`,
          response: `{
  "analysisId": "analysis-123",
  "results": [
    {
      "type": "alert",
      "message": "Temperature is above threshold",
      "severity": "high"
    }
  ],
  "recommendations": [
    {
      "action": "reduce_power",
      "description": "Reduce device power consumption"
    }
  ]
}`,
        },
      },
    ],
  },
  {
    id: "devices",
    name: "设备管理",
    description: "管理设备信息和状态的API",
    endpoints: [
      {
        id: "devices-list",
        method: "GET",
        path: "/api/v1/devices",
        summary: "获取设备列表",
        description: "获取用户所有设备的列表",
        response: {
          total: "number",
          devices: [
            {
              id: "device001",
              name: "Smart Thermostat",
              status: "online",
              lastSeen: "string",
            },
          ],
        },
        example: {
          request: `curl -X GET "/api/v1/devices" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`,
          response: `{
  "total": 1,
  "devices": [
    {
      "id": "device001",
      "name": "Smart Thermostat",
      "status": "online",
      "lastSeen": "2024-01-01T12:00:00Z"
    }
  ]
}`,
        },
      },
      {
        id: "devices-detail",
        method: "GET",
        path: "/api/v1/devices/{deviceId}",
        summary: "获取设备详情",
        description: "获取指定设备的详细信息",
        response: {
          id: "string",
          name: "string",
          status: "string",
          lastSeen: "string",
          specifications: {
            model: "string",
            firmwareVersion: "string",
          },
        },
        example: {
          request: `curl -X GET "/api/v1/devices/device001" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`,
          response: `{
  "id": "device001",
  "name": "Smart Thermostat",
  "status": "online",
  "lastSeen": "2024-01-01T12:00:00Z",
  "specifications": {
    "model": "ST-1000",
    "firmwareVersion": "1.2.3"
  }
}`,
        },
      },
    ],
  },
];

// 错误码数据
const errorCodes = [
  { code: 400, message: "Bad Request", description: "请求参数错误或格式无效" },
  { code: 401, message: "Unauthorized", description: "未提供有效的认证令牌" },
  { code: 403, message: "Forbidden", description: "没有权限访问该资源" },
  { code: 404, message: "Not Found", description: "请求的资源不存在" },
  {
    code: 500,
    message: "Internal Server Error",
    description: "服务器内部错误",
  },
  { code: 502, message: "Bad Gateway", description: "网关错误" },
  { code: 503, message: "Service Unavailable", description: "服务暂时不可用" },
];

// 认证方式数据
const authMethods = [
  {
    name: "注册要求",
    description: "",
    usage: "注意：必须通过企业邮箱进行身份认证",
    security: "medium",
    flowSteps: [
      "用户使用企业邮箱注册 / 登录",
      "获取X-API-Key",
      "在请求头中添加 X-API-Key: YOUR_API_KEY",
    ],
  },
  {
    name: "快速开始",
    description: "通过OAuth 2.0令牌进行认证",
    security: "high",
    flowSteps: [
      "注册 / 登录，自动开通24h免费试用",
      '点击"下载SDK"',
      "选择 Linux / Windows / macOS",
      "下载打开 App → 用户指南",
      "试用结束后选择套餐购买",
    ],
  },
];

const API = () => {
  const { isLoggedIn } = useUserStore();
  const [isSdkModalVisible, setIsSdkModalVisible] = useState(false);
  const navigate = useNavigate();

  const handleDownloadSDK = () => {
    if (!isLoggedIn) {
      navigate(`/login?redirect=${encodeURIComponent("/api-doc")}`);
      return;
    }
    setIsSdkModalVisible(true);
  };

  const handleModalClose = () => {
    setIsSdkModalVisible(false);
  };

  const handleViewGuide = () => {
    window.open("/guideZH.html", "_blank");
  };

  const handleSdkDownload = async (platform) => {
    if (platform !== "Linux") {
      message.info(`${platform}版本SDK尚未发布，敬请期待！`);
      return;
    }

    try {
      message.info(`正在下载${platform}版本SDK...`);
      const blob = await apiService.downloadApk();

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `stabilize-sdk-${platform.toLowerCase()}.apk`
      );

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`${platform}版本SDK下载成功！`);
    } catch (error) {
      console.error("SDK下载失败:", error);
      message.error(`SDK下载失败: ${error.message || "请稍后重试"}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航/英雄区 */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <div className="container mx-auto px-4 pb-4">
          <div className="max-w-5xl mx-auto text-center">
            <Title
              level={1}
              className="mb-4 text-3xl md:text-4xl font-bold text-slate-800"
            >
              API 文档
            </Title>
            <div className="text-gray-600 py-6 text-base md:text-lg max-w-3xl mx-auto">
              全面的API文档，帮助您快速集成我们的AI智能服务，赋能您的电子产品。
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-2 mb-6">
              <Button
                variant="blue-griadient"
                size="lg"
                onClick={handleDownloadSDK}
              >
                <Download className="mr-2 h-5 w-5" />
                下载SDK
              </Button>
              <Button variant="secondary" size="lg" onClick={handleViewGuide}>
                <Code className="mr-2 h-5 w-5" />
                查看文档
              </Button>
            </div>

            {/* SDK下载弹窗 */}
            <SDKDownloadModal
              visible={isSdkModalVisible}
              onClose={handleModalClose}
              onDownload={handleSdkDownload}
            />
          </div>
        </div>
      </section>

      {/* API概览 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap -mx-4">
              <div className="w-full md:w-1/3 px-4 mb-8 md:mb-0">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Database className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    RESTful API
                  </h3>
                  <p className="text-slate-500">
                    基于RESTful设计原则，提供简洁、易用、高效的API接口。
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4 mb-8 md:mb-0">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Shield className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    安全可靠
                  </h3>
                  <p className="text-slate-500">
                    采用OAuth 2.0认证机制，确保数据传输和存储的安全性。
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Clock className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    高可用性
                  </h3>
                  <p className="text-slate-500">
                    99.9%的可用性保证，确保您的服务持续稳定运行。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 主体内容 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* 认证方式 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <Lock className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">认证方式</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {authMethods.map((method, index) => (
                  <div
                    key={index}
                    className="p-6 bg-white rounded-lg shadow-sm border border-slate-200"
                  >
                    <div className="mb-3">
                      <h3 className="font-bold text-slate-800">
                        {method.name}
                      </h3>
                    </div>
                    {method.flowSteps ? (
                      <div className="space-y-3">
                        {method.usage && (
                          <div className="p-3 bg-slate-50 rounded text-sm font-mono text-slate-700 mb-3">
                            {method.usage}
                          </div>
                        )}
                        <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 pl-2">
                          {method.flowSteps.map((step, stepIndex) => (
                            <li key={stepIndex} className="leading-relaxed">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : (
                      <>
                        <p className="text-slate-500 mb-3">
                          {method.description}
                        </p>
                        <div className="p-3 bg-slate-50 rounded text-sm font-mono text-slate-700">
                          {method.usage}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* API 端点 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">API 端点</h2>
              </div>
              <Tabs defaultActiveKey="auth" type="card" className="mb-8">
                {apiCategories.map((category) => (
                  <Tabs.TabPane key={category.id} tab={category.name}>
                    <div className="mb-6">
                      <Text type="secondary">{category.description}</Text>
                    </div>
                    <Collapse defaultActiveKey={[category.endpoints[0].id]}>
                      {category.endpoints.map((endpoint) => (
                        <Panel
                          key={endpoint.id}
                          header={
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <span
                                  className={`inline-block px-3 py-1 text-xs font-bold rounded-full mr-3 ${
                                    endpoint.method === "GET"
                                      ? "bg-green-100 text-green-800"
                                      : endpoint.method === "POST"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-purple-100 text-purple-800"
                                  }`}
                                >
                                  {endpoint.method}
                                </span>
                                <strong>{endpoint.path}</strong>
                              </div>
                              <span className="text-sm text-slate-500">
                                {endpoint.summary}
                              </span>
                            </div>
                          }
                        >
                          <div className="space-y-6">
                            <div>
                              <h4 className="font-bold text-slate-800 mb-2">
                                描述
                              </h4>
                              <p className="text-slate-600">
                                {endpoint.description}
                              </p>
                            </div>

                            {endpoint.request && (
                              <div>
                                <h4 className="font-bold text-slate-800 mb-2">
                                  启动命令
                                </h4>
                                <div className="bg-slate-50 p-4 rounded-lg">
                                  <pre className="text-sm font-mono text-slate-700 overflow-x-auto">
                                    {JSON.stringify(endpoint.request, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}

                            <div>
                              <h4 className="font-bold text-slate-800 mb-2">
                                响应格式
                              </h4>
                              <div className="bg-slate-50 p-4 rounded-lg">
                                <pre className="text-sm font-mono text-slate-700 overflow-x-auto">
                                  {JSON.stringify(endpoint.response, null, 2)}
                                </pre>
                              </div>
                            </div>

                            <div>
                              <h4 className="font-bold text-slate-800 mb-2">
                                示例代码
                              </h4>
                              <Tabs defaultActiveKey="request">
                                <Tabs.TabPane tab="请求" key="request">
                                  <div className="bg-slate-900 p-4 rounded-lg">
                                    <pre className="text-sm text-slate-200 overflow-x-auto">
                                      {endpoint.example.request}
                                    </pre>
                                  </div>
                                </Tabs.TabPane>
                                <Tabs.TabPane tab="响应" key="response">
                                  <div className="bg-slate-900 p-4 rounded-lg">
                                    <pre className="text-sm text-slate-200 overflow-x-auto">
                                      {endpoint.example.response}
                                    </pre>
                                  </div>
                                </Tabs.TabPane>
                              </Tabs>
                            </div>
                          </div>
                        </Panel>
                      ))}
                    </Collapse>
                  </Tabs.TabPane>
                ))}
              </Tabs>
            </div>

            {/* 错误码 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <CheckCircle className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">错误码</h2>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <Table
                  dataSource={errorCodes}
                  columns={[
                    {
                      title: "错误码",
                      dataIndex: "code",
                      key: "code",
                      width: 100,
                    },
                    { title: "错误信息", dataIndex: "message", key: "message" },
                    {
                      title: "描述",
                      dataIndex: "description",
                      key: "description",
                    },
                  ]}
                  pagination={false}
                  size="middle"
                />
              </div>
            </div>

            {/* 使用指南 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <ArrowRight className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">快速开始</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    1
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">获取API密钥</h3>
                  <p className="text-slate-500">
                    在控制台中创建应用并获取API密钥或令牌。
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    2
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">
                    选择集成方式
                  </h3>
                  <p className="text-slate-500">
                    选择适合您项目的SDK或直接调用REST API。
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    3
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">开始开发</h3>
                  <p className="text-slate-500">
                    参考文档开始开发，利用我们的API构建智能应用。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 免费试用/CTA
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-white mb-6 text-2xl">开始使用我们的API</div>
            <div className="text-blue-100 mb-12">
              免费试用我们的API，体验强大的AI智能服务，赋能您的电子产品。
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="secondary" size="lg">
                <ArrowRight className="mr-2 h-5 w-5" />
                开始试用
              </Button>
            </div>
          </div>
        </div>
      </section> */}

      {/* 页脚 */}
      <Footer />
    </div>
  );
};

export default API;
